import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { AxiosError } from 'axios';

import { CACHE_KEY_PHARMAKON_V3_USER } from 'src/app/hooks/data/_keys';
import { useBffClient } from 'src/app/hooks/data/useBffClient';
import type { User, UserPasswordCheck } from 'src/app/types/user';

export const useUser = (
  userUuid: string,
  enabled = false,
  retry = 3,
  refetchOnWindowFocus = true //Default value
): UseQueryResult<User, AxiosError> => {
  const authClient = useBffClient();

  return useQuery(
    [CACHE_KEY_PHARMAKON_V3_USER, userUuid],
    () => authClient.get(`/auth/user/${userUuid}`).then((resp: { data: User }) => resp.data),
    {
      retry,
      enabled: enabled && Boolean(userUuid),
      refetchOnWindowFocus,
    }
  );
};

export const useUserCheckPassword = (): UseMutationResult<UserPasswordCheck, AxiosError, string> => {
  const authClient = useBffClient();

  return useMutation((password: string) =>
    authClient
      .post(
        `/auth/user/check_password`,
        {
          password,
        },
        { headers: { 'Content-Type': 'application/json' } }
      )
      .then((resp: { data: UserPasswordCheck }) => resp.data)
  );
};

interface CheckEmailParams {
  email: string;
  captchaToken: string;
  captchaVersion?: undefined | 'v2'; // When "invisible" v3 Captcha fails, we try the "v2" version as a backup.
}

export const useCheckEmailAvailability = (): UseMutationResult<void, AxiosError, CheckEmailParams> => {
  const authClient = useBffClient();

  return useMutation(({ email, captchaToken, captchaVersion }) =>
    authClient.post(`/accounts/email_check`, { email, captchaToken, ...(!!captchaVersion && { captchaVersion }) })
  );
};
